import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Breadcrumbs,
  Link,
  Avatar,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Groups as GroupsIcon,
  PersonRemove as PersonRemoveIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Team } from '@risk-management/shared';
import { teamsService } from '../services/teams.service';
import { userService, User } from '../services/user.service';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role?: string;
  joinedAt?: string;
}

export const TeamDetail: React.FC = () => {
  const { t } = useTranslation();
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog states
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Alert state
  const [alertMessage, setAlertMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Fetch team details
  const {
    data: team,
    isLoading: teamLoading,
    error: teamError,
  } = useQuery<Team>({
    queryKey: ['team', teamId],
    queryFn: () => teamsService.getWithMembers(teamId ?? ''),
    enabled: !!teamId,
  });

  // Fetch team members
  const {
    data: members,
    isLoading: membersLoading,
    error: membersError,
  } = useQuery<TeamMember[]>({
    queryKey: ['teamMembers', teamId],
    queryFn: () => teamsService.getMembers(teamId ?? ''),
    enabled: !!teamId,
  });

  // Fetch all users for add member dialog
  const { data: allUsers } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => userService.getAll(),
    enabled: addMemberDialogOpen,
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: (userId: string) =>
      teamsService.addMember(teamId ?? '', userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      queryClient.invalidateQueries({ queryKey: ['adminTeams'] });
      setAlertMessage({ type: 'success', text: t('admin.teams.memberAdded') });
      setAddMemberDialogOpen(false);
      setSelectedUser(null);
    },
    onError: () => {
      setAlertMessage({
        type: 'error',
        text: t('admin.teams.addMemberError'),
      });
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) =>
      teamsService.removeMember(teamId ?? '', userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      queryClient.invalidateQueries({ queryKey: ['adminTeams'] });
      setAlertMessage({
        type: 'success',
        text: t('admin.teams.memberRemoved'),
      });
      setRemoveMemberDialogOpen(false);
      setSelectedMember(null);
    },
    onError: () => {
      setAlertMessage({
        type: 'error',
        text: t('admin.teams.removeMemberError'),
      });
    },
  });

  const handleAddMember = () => {
    if (selectedUser) {
      addMemberMutation.mutate(selectedUser.id);
    }
  };

  const handleRemoveMember = () => {
    if (selectedMember) {
      removeMemberMutation.mutate(selectedMember.id);
    }
  };

  const handleRemoveClick = (member: TeamMember) => {
    setSelectedMember(member);
    setRemoveMemberDialogOpen(true);
  };

  // Filter members by search query
  const filteredMembers = members?.filter(
    member =>
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter available users (exclude already members)
  const availableUsers = allUsers?.filter(
    (user: User) => !members?.some(member => member.id === user.id)
  );

  const formatDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const isLoading = teamLoading || membersLoading;
  const error = teamError || membersError;

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !team) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{t('admin.teams.loadError')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/admin"
          underline="hover"
          color="inherit"
        >
          {t('admin.dashboard.title')}
        </Link>
        <Link
          component={RouterLink}
          to="/admin/teams"
          underline="hover"
          color="inherit"
        >
          {t('admin.teams.title')}
        </Link>
        <Typography color="text.primary">{team.name}</Typography>
      </Breadcrumbs>

      {/* Alert */}
      {alertMessage && (
        <Alert
          severity={alertMessage.type}
          onClose={() => setAlertMessage(null)}
          sx={{ mb: 3 }}
        >
          {alertMessage.text}
        </Alert>
      )}

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin/teams')}>
            <ArrowBackIcon />
          </IconButton>
          <GroupsIcon color="primary" sx={{ fontSize: 40 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5">{team.name}</Typography>
            {team.description && (
              <Typography variant="body2" color="text.secondary">
                {team.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {team.isScimManaged && (
              <Chip
                label={t('admin.teams.scimManaged')}
                color="info"
                size="small"
              />
            )}
            <Chip
              label={t('admin.teams.memberCountLabel', {
                count: members?.length ?? 0,
              })}
              variant="outlined"
            />
          </Box>
        </Box>
      </Paper>

      {/* Members Section */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h6">{t('admin.teams.members')}</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              placeholder={t('common.search')}
              size="small"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddMemberDialogOpen(true)}
              disabled={team.isScimManaged}
            >
              {t('admin.teams.addMember')}
            </Button>
          </Box>
        </Box>

        {team.isScimManaged && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('admin.teams.scimManagedInfo')}
          </Alert>
        )}

        {/* Members Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.user.name')}</TableCell>
                <TableCell>{t('admin.user.email')}</TableCell>
                <TableCell>{t('admin.user.role')}</TableCell>
                <TableCell>{t('admin.teams.joinedAt')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMembers?.map(member => (
                <TableRow key={member.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {member.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography>{member.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={member.role || 'user'}
                      size="small"
                      color={member.role === 'admin' ? 'secondary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDate(member.joinedAt)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveClick(member)}
                      disabled={team.isScimManaged}
                      title={t('admin.teams.removeMember')}
                    >
                      <PersonRemoveIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(!filteredMembers || filteredMembers.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('admin.teams.noMembers')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Member Dialog */}
      <Dialog
        open={addMemberDialogOpen}
        onClose={() => {
          setAddMemberDialogOpen(false);
          setSelectedUser(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('admin.teams.addMember')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={availableUsers || []}
              getOptionLabel={user => `${user.name} (${user.email})`}
              value={selectedUser}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('admin.teams.selectUser')}
                  fullWidth
                />
              )}
              renderOption={(props, user) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                      {user.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{user.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddMemberDialogOpen(false);
              setSelectedUser(null);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            disabled={!selectedUser || addMemberMutation.isPending}
          >
            {addMemberMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('common.add')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <Dialog
        open={removeMemberDialogOpen}
        onClose={() => {
          setRemoveMemberDialogOpen(false);
          setSelectedMember(null);
        }}
      >
        <DialogTitle>{t('admin.teams.removeMemberTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('admin.teams.removeMemberConfirm', {
              name: selectedMember?.name,
              team: team.name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveMemberDialogOpen(false);
              setSelectedMember(null);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleRemoveMember}
            variant="contained"
            color="error"
            disabled={removeMemberMutation.isPending}
          >
            {removeMemberMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('common.remove')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
